/**
 * Hero da plataforma UCC (Editorial Esportivo de Alta Performance).
 *
 * Inspirado nas referências da Refero Design (Oryzo AI & Hyer Aviation):
 *   - Imersão total: Campo de futebol com scrub dinâmico (play/reverse play) em 1080p WebP
 *   - Tipografia monumental com gradiente metálico dourado
 *   - Card em vidro com os campeonatos reais: carregando, vazio ou preenchido
 *   - Efeitos de luz de estádio (glow de refletores e paralaxe no mouse)
 */

import { useRef } from 'react';
import { Link } from 'react-router';
import { countUp, gsap, useGSAP, EASE, DURATION } from '@/lib/motion';
import { HeroEmbers } from './ui/HeroEmbers';
import { TrophyMark } from './TrophyMark';
import type { TournamentSummary } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { NumerosDaPlataforma } from './NumerosDaPlataforma';

interface HeroProps {
  featured: TournamentSummary[];
  loading?: boolean;
}


export function Hero({ featured, loading = false }: HeroProps) {
  const { isStaff } = useAuth();
  const root = useRef<HTMLElement>(null);
  /*
   * Sem invenção: o que aparece aqui é o que a API devolveu.
   *
   * Havia um FALLBACK_TOURNAMENTS com duas edições fictícias — nome, premiação,
   * vagas e slug — que entravam em cena sempre que a lista real vinha vazia. O
   * card ficava bonito e o botão "Garantir vaga" apontava para um endereço que
   * não existe. Uma tela vazia é constrangedora; um capitão tentando se
   * inscrever num campeonato que nunca existiu é outra coisa.
   */
  const activeTournaments = featured;

  useGSAP(
    (_contexto, contextSafe) => {
      const tl = gsap.timeline({ defaults: { ease: EASE.signature } });

      tl.from('[data-anim="headline-line"]', {
        yPercent: 110,
        duration: DURATION.hero,
        stagger: 0.08,
      })
        .from('[data-anim="subcopy"]', { y: 16, autoAlpha: 0, duration: 0.7 }, '-=0.55')
        .from('[data-anim="cta"]', { y: 14, autoAlpha: 0, stagger: 0.08, duration: 0.6 }, '-=0.45')
        .from('[data-anim="card"]', { y: 30, autoAlpha: 0, stagger: 0.12, duration: 0.8 }, '-=0.5')
        .from('[data-anim="stats-strip"]', { y: 14, autoAlpha: 0, duration: 0.6 }, '-=0.3');

      const reduzido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduzido) return;

      // Ambiente contínuo com refletores derivando
      gsap.to('[data-anim="glow-a"]', {
        xPercent: 10,
        yPercent: -8,
        duration: 12,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
      gsap.to('[data-anim="glow-b"]', {
        xPercent: -8,
        yPercent: 6,
        duration: 16,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });

      // Paralaxe do mouse
      if (!window.matchMedia('(pointer: fine)').matches) return;

      const paraX = gsap.quickTo('[data-anim="parallax"]', 'x', {
        duration: 1.2,
        ease: 'power3.out',
      });
      const paraY = gsap.quickTo('[data-anim="parallax"]', 'y', {
        duration: 1.2,
        ease: 'power3.out',
      });

      const mover = (evento: MouseEvent) => {
        const alcance = 28;
        paraX((evento.clientX / window.innerWidth - 0.5) * alcance * 2);
        paraY((evento.clientY / window.innerHeight - 0.5) * alcance);
      };

      const aoMover = (contextSafe?.(mover) ?? mover) as (evento: MouseEvent) => void;
      window.addEventListener('mousemove', aoMover, { passive: true });
      return () => window.removeEventListener('mousemove', aoMover);
    },
    { scope: root },
  );

  // Contador numérico nas vagas dos cards
  useGSAP(
    () => {
      gsap.utils.toArray<HTMLElement>('[data-count]').forEach((el) => {
        countUp(el, Number(el.dataset['count'] ?? 0), { duration: 1.0 });
      });
    },
    { scope: root, dependencies: [activeTournaments.map((t) => t.id).join(',')] },
  );

  return (
    <section
      ref={root}
      /*
       * Uma tela de altura, e não mais grudada: a hero rola para fora como
       * qualquer seção. `min-h` com `items-start` deixa a seção crescer quando
       * o conteúdo não cabe — com altura travada e conteúdo centralizado, o
       * excedente saía para fora da tela e o título atravessava o cabeçalho.
       *
       * `svh` e não `dvh`: o `dvh` muda de valor quando a barra do navegador
       * some ao rolar, e altura que muda durante o gesto faz a seção saltar.
       */
      className="ucc-grain relative isolate flex min-h-[100svh] items-start justify-center overflow-hidden bg-ink pt-28 pb-16 lg:items-center lg:py-0"
    >
      {/*
        Brasas no lugar do vídeo.
        ────────────────────────
        O fundo era uma sequência de 100 quadros WebP desenhada num plano WebGL
        com shader. Bonito e caro: 1,7 MB baixados na abertura, uma textura de
        tela cheia repintada a cada quadro, e a rolagem travando mesmo depois de
        limitar resolução e pausar fora da tela.

        As brasas custam uma fração disso — cinquenta partículas num canvas 2D,
        sem download nenhum — e dizem a mesma coisa que o gramado dizia: que
        aqui tem competição acontecendo. O ambiente que já existia por trás do
        vídeo (véus, refletores, linhas de campo) segue sendo o cenário.
      */}
      <HeroEmbers />

      <div aria-hidden className="pointer-events-none absolute inset-0 -z-5 overflow-hidden">
        {/*
          Vinhetas cinematográficas de alto contraste.

          As duas trabalham por subtração: o gramado aparece no meio e o texto
          fica sobre a parte apagada. No tema claro a mesma receita não serve —
          véu branco a 15% no meio não apaga nada, só lava o vídeo e deixa uma
          faixa suja atravessando a cena. As classes `ucc-hero-veu-*` existem
          para o tema claro refazer os dois degradês com opacidade alta e
          uniforme: lá o gramado vira textura de fundo, não protagonista.
        */}
        <div className="ucc-hero-veu-vertical absolute inset-0 bg-linear-to-b from-ink/80 via-ink/15 to-ink/90" />
        <div className="ucc-hero-veu-horizontal absolute inset-0 bg-linear-to-r from-ink/90 via-ink/45 to-transparent" />
        <div className="ucc-stadium-glow absolute inset-0 opacity-70" />
        <div className="ucc-grid-lines absolute inset-0 opacity-25" />

        {/*
          Refletores com paralaxe.

          O desfoque saiu daqui, e essa é a diferença entre rolar liso e rolar
          travado. `blur-[140px]` sobre um círculo de 704px obriga o navegador a
          rasterizar e borrar uma superfície enorme, e a refazer isso sempre que
          a camada muda de posição — que é exatamente o que acontece durante a
          rolagem, com dois deles em cena.

          Um `radial-gradient` chega ao mesmo halo difuso: ele já nasce suave,
          é desenhado uma vez e não passa por filtro nenhum.
        */}
        <div data-anim="parallax" className="absolute inset-0 will-change-transform">
          <div
            data-anim="glow-a"
            className="absolute -top-32 left-1/4 h-[44rem] w-[44rem] rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgb(248 212 40 / 0.16) 0%, rgb(248 212 40 / 0.07) 38%, transparent 68%)',
            }}
          />
          <div
            data-anim="glow-b"
            className="absolute right-0 -bottom-40 h-[40rem] w-[40rem] rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgb(184 134 11 / 0.22) 0%, rgb(184 134 11 / 0.09) 40%, transparent 70%)',
            }}
          />
        </div>

        <div className="absolute inset-x-0 bottom-0 h-px bg-linear-to-r from-transparent via-gold/40 to-transparent" />
      </div>


      <div className="mx-auto grid w-full max-w-[92rem] gap-12 px-6 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:gap-16 xl:gap-20 xl:px-12 2xl:max-w-[104rem]">
        {/* Coluna Principal de Mensagem */}
        <div>
          <h1 className="ucc-display text-[clamp(2.5rem,5.5vw,5.2rem)] leading-[0.88] tracking-tight">
            <span className="-mt-[0.14em] block overflow-hidden pt-[0.14em] pb-[0.06em]">
              <span data-anim="headline-line" className="block whitespace-nowrap">
                A Arena do
              </span>
            </span>
            <span className="-mt-[0.14em] block overflow-hidden pt-[0.14em] pb-[0.06em]">
              <span data-anim="headline-line" className="block whitespace-nowrap">
                <span className="ucc-gold-text">Futebol Virtual</span>
                <span className="text-gold-ink">.</span>
              </span>
            </span>
          </h1>

          <p data-anim="subcopy" className="mt-7 max-w-lg text-lg leading-relaxed text-muted">
            Sorteios 100% auditáveis com hash criptográfico, chaveamentos automáticos e estatísticas oficiais sincronizadas direto do EA SPORTS FC.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            {/* O botão leva ao cadastro do time, que é o que ele promete.
                Antes ia para a lista de campeonatos, e de lá a pessoa ainda
                tinha de achar sozinha o caminho de criar conta e cadastrar
                equipe. Para quem organiza o destino continua sendo a lista:
                staff não se inscreve. */}
            <Link
              data-anim="cta"
              to={isStaff ? '/campeonatos' : '/cadastrar-time'}
              className="group relative inline-flex items-center gap-2.5 rounded-xl bg-gold px-8 py-4 font-bold tracking-wide text-black uppercase shadow-[var(--shadow-gold)] transition-all duration-[--duration-quick] hover:bg-gold-bright hover:scale-[1.02]"
            >
              {isStaff ? 'Ver campeonatos' : 'Inscrever meu time'}
              <svg
                className="size-4 transition-transform duration-[--duration-quick] group-hover:translate-x-1"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden
              >
                <path
                  d="M2 8h11M9 4l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>

            <Link
              data-anim="cta"
              to="/regulamento"
              className="inline-flex items-center rounded-xl border border-line-strong bg-surface/92 px-7 py-4 font-semibold tracking-wide uppercase text-paper transition-all duration-[--duration-quick] hover:border-gold/50 hover:bg-surface-2"
            >
              Como funciona
            </Link>
          </div>
        </div>

        {/* Coluna Lateral: Destaques de Campeonatos */}
        <div className="relative">
          {/* Marca d'água de Troféu */}
          <div
            data-anim="trophy"
            aria-hidden
            className="pointer-events-none absolute top-1/2 right-[-5rem] -z-10 hidden -translate-y-1/2 lg:block"
          >
            <div
              className="absolute inset-8 -z-10 rounded-full"
              style={{
                background:
                  'radial-gradient(circle, rgb(248 212 40 / 0.18) 0%, rgb(248 212 40 / 0.06) 45%, transparent 72%)',
              }}
            />
            <TrophyMark className="h-[32rem] w-auto opacity-[0.14]" />
          </div>

          <div className="flex flex-col gap-4">
            <header className="flex items-baseline justify-between">
              <div className="flex items-center gap-2">
                <span className="size-2 rounded-full bg-gold shadow-[0_0_8px_var(--color-gold)]" />
                <h2 className="ucc-display text-2xl tracking-wide text-paper">Inscrições Abertas</h2>
              </div>
              <Link
                to="/campeonatos"
                className="text-sm font-medium text-muted transition-colors hover:text-gold-ink"
              >
                ver todos os torneios →
              </Link>
            </header>

            {loading ? (
              <>
                <CardEsqueleto />
                <CardEsqueleto />
              </>
            ) : activeTournaments.length === 0 ? (
              <SemInscricoes />
            ) : (
              activeTournaments.slice(0, 2).map((tournament) => (
                <TournamentCard key={tournament.id} tournament={tournament} />
              ))
            )}
          </div>
        </div>

        {/*
          Faixa de números, nas duas colunas.

          Antes os números moravam numa seção própria logo abaixo da hero, e
          a hero terminava num espaço vazio com o selo de auditável sozinho.
          Trazidos para cá, eles são a primeira prova de que a plataforma
          roda de verdade -- e continuam sendo os números do banco, não os
          "128+ clubes" inventados que já ocuparam este lugar.
        */}
        <div
          data-anim="stats-strip"
          className="flex flex-col gap-6 border-t border-line/60 pt-6 lg:col-span-2"
        >
          <div className="flex items-center gap-2 text-xs text-muted">
            <span className="size-1.5 rounded-full bg-win" />
            <span>Chaveamento 100% Auditável</span>
          </div>
          <NumerosDaPlataforma naHero />
        </div>
      </div>
    </section>
  );
}

function CardEsqueleto() {
  return <div data-anim="card" aria-hidden className="ucc-panel h-[13.5rem] animate-pulse rounded-xl" />;
}

function SemInscricoes() {
  return (
    <div data-anim="card" className="ucc-panel p-8 text-center">
      <p className="text-paper">Nenhuma inscrição aberta no momento.</p>
      <p className="mt-1.5 text-sm text-muted">
        A próxima edição é anunciada no Discord da UCC assim que as datas fecham.
      </p>
    </div>
  );
}

function TournamentCard({ tournament }: { tournament: TournamentSummary }) {
  const { isStaff } = useAuth();
  const full = tournament.spotsLeft === 0;
  const almostFull = tournament.spotsLeft > 0 && tournament.spotsLeft <= 3;
  const filled = tournament.capacity - tournament.spotsLeft;
  const pct = Math.round((filled / tournament.capacity) * 100);

  return (
    <article
      data-anim="card"
      className="ucc-gold-border group relative overflow-hidden rounded-2xl bg-surface/95 p-6 transition-all duration-[--duration-standard] hover:-translate-y-1 hover:shadow-[0_16px_36px_-10px_rgba(248,212,40,0.25)]"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="ucc-eyebrow text-[0.6875rem] text-gold-ink font-semibold">
            {tournament.game === 'fc26' ? 'EA SPORTS FC 26' : 'EA SPORTS FC 27'}
          </span>
          <h3 className="ucc-display mt-1.5 text-2xl text-paper">{tournament.name}</h3>
          <p className="mt-1 text-xs text-muted font-mono">{formatDateTime(tournament.startsAt)}</p>
        </div>
        <PrizeBadge cents={tournament.prizePoolCents} />
      </div>

      <div className="mt-5">
        <div className="flex items-baseline justify-between text-xs">
          <span className="text-muted">
            <span data-count={filled} className="tabular font-bold text-paper">
              {filled}
            </span>
            <span className="text-faint"> / {tournament.capacity} vagas preenchidas</span>
          </span>
          <span
            className={
              full
                ? 'font-bold text-loss'
                : almostFull
                  ? 'font-bold text-gold-ink'
                  : 'text-faint'
            }
          >
            {full
              ? 'Lotado'
              : almostFull
                ? `Últimas ${tournament.spotsLeft} vagas!`
                : `${tournament.spotsLeft} disponíveis`}
          </span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-surface-3">
          <div
            className="h-full rounded-full bg-linear-to-r from-gold-deep via-gold to-gold-bright transition-[width] duration-[--duration-hero] ease-[--ease-signature]"
            style={{ width: `${pct}%` }}
            role="progressbar"
            aria-valuenow={filled}
            aria-valuemin={0}
            aria-valuemax={tournament.capacity}
          />
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        {/* Ver o comentário na página do campeonato: staff não se inscreve. */}
        {!isStaff && (
        <Link
          to={`/campeonatos/${tournament.slug}/inscricao`}
          aria-disabled={full}
          className={
            full
              ? 'pointer-events-none flex-1 rounded-xl border border-line bg-surface-2 py-3 text-center text-xs font-bold text-faint uppercase'
              : 'flex-1 rounded-xl bg-gold py-3 text-center text-xs font-bold text-black uppercase transition-all duration-[--duration-quick] hover:bg-gold-bright hover:shadow-[var(--shadow-gold)]'
          }
        >
          {full ? 'Vagas Esgotadas' : `Garantir Vaga — ${formatMoney(tournament.entryFeeCents)}`}
        </Link>
        )}
        <Link
          to={`/campeonatos/${tournament.slug}`}
          className={
            isStaff
              ? 'flex-1 rounded-xl border border-line py-3 text-center text-xs font-medium text-muted transition-colors duration-[--duration-quick] hover:border-gold/50 hover:text-paper'
              : 'rounded-xl border border-line px-4 py-3 text-xs font-medium text-muted transition-colors duration-[--duration-quick] hover:border-gold/50 hover:text-paper'
          }
        >
          Regras
        </Link>
      </div>
    </article>
  );
}

function PrizeBadge({ cents }: { cents: number }) {
  if (cents <= 0) return null;
  return (
    <div className="shrink-0 rounded-xl border border-gold/30 bg-gold/10 px-3.5 py-2 text-right">
      <p className="text-[0.625rem] font-bold tracking-wider text-gold-ink/90 uppercase">Premiação</p>
      <p className="ucc-display tabular text-xl text-gold-ink font-bold">{formatMoney(cents)}</p>
    </div>
  );
}

function formatMoney(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  });
}

function formatDateTime(epochSeconds: number | null): string {
  if (!epochSeconds) return 'Data a definir';
  return new Date(epochSeconds * 1000)
    .toLocaleString('pt-BR', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
    .replace(',', ' ·');
}

